import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import dayjs from "dayjs";
import { confirmAlert } from "react-confirm-alert";
import { Link } from "react-router-dom";
import { findByNumeroBimestre, update, remove } from "./api-bimestre";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { errorToast, success } from "../../util/ToastUtil";
import Loading from "../UI/Loading";
import TextFieldDate from "../UI/TextFieldDate";

const BIMESTRES = [1, 2, 3, 4];

// O seletor dispara onChange a cada tecla; só salva quando a data estiver completa.
const isDataCompleta = (value) => value && dayjs(value).isValid() && dayjs(value).year() >= 1900;

export default function Bimestre() {
  const [numeroBimestre, setNumeroBimestre] = useState(1);
  const [bimestres, setBimestres] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingCodigo, setSavingCodigo] = useState(null);
  const [salvoCodigo, setSalvoCodigo] = useState(null);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    findByNumeroBimestre(numeroBimestre)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
          setBimestres([]);
        } else if (Array.isArray(data)) {
          setBimestres(
            [...data].sort((a, b) => (a.serie?.descricao ?? "").localeCompare(b.serie?.descricao ?? "", "pt-BR")),
          );
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [numeroBimestre]);

  const handleDataChange = (bimestre, campo, value) => {
    const atualizado = { ...bimestre, [campo]: value ? dayjs(value) : null };
    // Atualiza a tela na hora; salva só quando a data estiver completa.
    setBimestres((prevState) => prevState.map((b) => (b.codigo === bimestre.codigo ? atualizado : b)));
    if (!isDataCompleta(value)) return;

    const { dataInicio, dataFim } = atualizado;
    if (dataInicio && dataFim && dayjs(dataFim).isBefore(dayjs(dataInicio), "day")) {
      errorToast("A data fim não pode ser anterior à data início.");
      return;
    }

    setSavingCodigo(bimestre.codigo);
    update(atualizado)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível salvar a data.");
          return;
        }
        setSalvoCodigo(bimestre.codigo);
      })
      .finally(() => setSavingCodigo(null));
  };

  const handleDelete = (bimestre) => {
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover o ${numeroBimestre}º bimestre da série ${bimestre.serie?.descricao ?? ""}?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            remove(bimestre.codigo).then((data) => {
              if (data?.error) {
                errorMessage(data.message);
                return;
              }
              setBimestres((prevState) => prevState.filter((b) => b.codigo !== bimestre.codigo));
              success("Bimestre removido.");
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const renderTabela = () => {
    if (isLoading) {
      return (
        <Box py={6}>
          <Loading description="Carregando bimestres..." />
        </Box>
      );
    }
    if (bimestres.length === 0) {
      return (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            color: "text.secondary",
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <EventNoteOutlinedIcon fontSize="large" />
          <Typography variant="body2" mt={1}>
            Nenhuma série com o {numeroBimestre}º bimestre cadastrado.
          </Typography>
        </Box>
      );
    }
    return (
      <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
        <Table size="small" sx={{ minWidth: 640 }} aria-label={`${numeroBimestre}º bimestre por série`}>
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
              <TableCell>Série</TableCell>
              <TableCell sx={{ width: 200 }}>Data Início</TableCell>
              <TableCell sx={{ width: 200 }}>Data Fim</TableCell>
              <TableCell sx={{ width: 90 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {bimestres.map((bimestre) => {
              const fimAntesDoInicio =
                bimestre.dataInicio &&
                bimestre.dataFim &&
                dayjs(bimestre.dataFim).isBefore(dayjs(bimestre.dataInicio), "day");
              return (
                <TableRow key={bimestre.codigo} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {bimestre.serie?.descricao}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <TextFieldDate
                      value={bimestre.dataInicio || null}
                      onChange={(value) => handleDataChange(bimestre, "dataInicio", value)}
                      slotProps={{ textField: { size: "small", "aria-label": "Data início" } }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <TextFieldDate
                      value={bimestre.dataFim || null}
                      onChange={(value) => handleDataChange(bimestre, "dataFim", value)}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: Boolean(fimAntesDoInicio),
                          "aria-label": "Data fim",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <Box width={24} display="flex" justifyContent="center">
                        {savingCodigo === bimestre.codigo ? (
                          <CircularProgress size={18} />
                        ) : (
                          salvoCodigo === bimestre.codigo && (
                            <Tooltip title="Salvo">
                              <CheckCircleOutlineIcon fontSize="small" color="success" />
                            </Tooltip>
                          )
                        )}
                      </Box>
                      <Tooltip title="Remover bimestre da série">
                        <IconButton size="small" onClick={() => handleDelete(bimestre)}>
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        mb={2}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={numeroBimestre}
          onChange={(event, value) => {
            if (!value) return;
            setSalvoCodigo(null);
            setNumeroBimestre(value);
          }}
          aria-label="Bimestre"
        >
          {BIMESTRES.map((b) => (
            <ToggleButton key={b} value={b}>
              {b}º Bimestre
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button variant="contained" startIcon={<AddIcon />} component={Link} to="/bimestre-form">
          Cadastrar Série
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary" component="div" mb={1}>
        As datas são salvas automaticamente ao serem alteradas.
      </Typography>

      {renderTabela()}
    </Box>
  );
}
